/**
 * Who is signed in and what they may do, from `GET /auth/me`.
 *
 * The role decides what the screen offers. It is a courtesy, not the control: every route checks
 * role and package scope itself, so a button enabled by mistake is refused by the server.
 */
import type { Schemas } from "@neio/contracts";
import { useQuery } from "@tanstack/react-query";
import { ApiError, request } from "./http";

export type Me = Schemas["MeView"];
export type Role = Me["role"];

const ORDER: Role[] = ["viewer", "engineer", "admin"];

export const ME_KEY = ["me"] as const;

/** The signed-in person, or null when there is no session. Never redirects. */
export async function fetchMe(): Promise<Me | null> {
  try {
    return await request<Me>("/auth/me", { signIn: false });
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 503)) return null;
    throw error;
  }
}

/** Who is signed in. `me` is undefined while loading and null with no session. */
export function useMe() {
  const query = useQuery({ queryKey: ME_KEY, queryFn: fetchMe, staleTime: 5 * 60_000 });
  return { me: query.data, loading: query.isPending, error: query.error };
}

export function atLeast(me: Me | null | undefined, role: Role): boolean {
  return !!me && ORDER.indexOf(me.role) >= ORDER.indexOf(role);
}

/** Why this person may not do something that needs `role`, or null if they may. */
export function roleBlock(me: Me | null | undefined, role: Role): string | null {
  if (me === undefined) return "Checking your sign-in.";
  if (!me) return "Sign in to do this.";
  if (atLeast(me, role)) return null;
  return role === "admin"
    ? `Admins only. You are signed in as ${me.role}.`
    : `Needs an engineer. You are signed in as ${me.role}, which is read-only.`;
}

export function initials(name: string | null | undefined): string {
  const words = (name ?? "").replace(/@.*/, "").split(/[\s._-]+/).filter(Boolean);
  return (words.length > 1 ? words[0][0] + words[words.length - 1][0] : (words[0] ?? "?").slice(0, 2)).toUpperCase();
}

/** End the session, then reload so every screen starts again from the sign-in gate. */
export async function signOut(reload: () => void = () => window.location.reload()): Promise<void> {
  try {
    await request("/auth/logout", { method: "POST", signIn: false });
  } finally {
    reload();
  }
}
